import type { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import { Config } from '../config';
import { Logger } from '../logger';
import { initChromecastMux, type MuxHandle } from './mux-sdk';

// The `automaticErrorTracking: false` flag below is deliberate: when `true`
// (the SDK default), the Chromecast plugin auto-emits an `error` event on every
// CAF `ERROR` — but with `player_error_message` taken from `error.message`,
// which the CAF `ErrorEvent` rarely carries. That is the source of the "Error
// did not include a message" entries in Mux. We disable it and emit enriched
// errors ourselves via {@link emitMuxError} so each error reaches Mux exactly
// once, with a readable message. See `handlePlayerError` in
// `player/event-listeners.ts`.

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

const LOG_TAG = 'MUX';

let muxHandle: MuxHandle | null = null;
let muxDisabled = false;

/**
 * Last rendition reported to Mux (`WxH@bitrate`), used to dedupe repeated
 * `renditionchange` emits. Reset on every new video so each play reports fresh.
 */
let lastRendition: string | null = null;

// `view_client_application_name` / `view_client_application_version` are set in
// BOTH `initMux` and `signalVideoChange`. The repetition is deliberate — do not
// factor it out or drop it from the videochange path.
//
// `videochange` does not reset the metadata directly; it happens one hop away,
// which is easy to miss when reading mux-embed:
//
//   emit('videochange', data)
//     -> _resetView(data)
//          -> emit('viewend')            flushes the view, clears dedup state
//          -> emit('viewinit', data)
//               -> _resetVideoData()     deletes every `video_`-prefixed key
//                  _resetViewData()      deletes every `view_`-prefixed key
//                  Object.assign(this.data, data)   re-applies ONLY this event's
//
// Init takes the same path (`emit('viewinit', options.data)`), and there is no
// persistent copy of the init `data` anywhere — `this.options` holds monitor
// config only. So a field sent once at init is reported for the first play of a
// Cast session and is absent from every queue-autoplay play after it.
//
// Verified empirically rather than by reading the minified bundle: `viewstart`
// bypasses beacon dedup and emits the full data object, so its payload is a
// direct read of `this.data`. After a videochange carrying only video_id and
// video_title, video-2's viewstart contains neither the `view_` nor the
// `video_` field. (Wire keys are shortened: `xcialve`, `vecva`.)
//
// This contradicts Mux's Chromecast guide, which says you "should only need to
// update the values that start with `video_`" on a new source. That is correct
// for `video_` fields and incomplete for `view_` ones.

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initializes Mux Data monitoring on the first media LOAD.
 *
 * Must be called exactly once per Cast session — subsequent plays in the
 * queue should use {@link signalVideoChange} instead.
 */
export function initMux(
  playerManager: PlayerManager,
  videoId: string,
  videoTitle: string,
  viewerUserId: string,
  videoEncodingVariant: string,
): void {
  if (muxHandle) {
    Logger.warn(LOG_TAG, 'Mux already initialized — skipping');
    return;
  }

  const envKey = Config.MUX_ENV_KEY;
  if (!envKey) {
    Logger.warn(LOG_TAG, 'MUX_ENV_KEY not set — Mux disabled');
    muxDisabled = true;
    return;
  }

  // initChromecastMux attaches a `.mux` handle to the playerManager
  initChromecastMux(playerManager, {
    debug: Config.DEBUG_MODE,
    // We own error reporting (see emitMuxError) to avoid double-counting.
    automaticErrorTracking: false,
    data: {
      env_key: envKey,
      player_name: 'cast',
      player_init_time: initChromecastMux.utils.now(),
      video_id: videoId,
      video_title: videoTitle,
      video_encoding_variant: videoEncodingVariant,
      viewer_user_id: viewerUserId,
      view_client_application_name: 'cast',
      view_client_application_version: Config.BUILD_VERSION,
    },
  });

  muxHandle = (playerManager as unknown as { mux: MuxHandle }).mux;
  if (!muxHandle) {
    Logger.error(LOG_TAG, 'Failed to get Mux handle from playerManager');
    muxDisabled = true;
    return;
  }

  lastRendition = null;
  Logger.info(LOG_TAG, 'Mux initialized', {
    videoId,
    videoTitle,
    viewerUserId,
    buildVersion: Config.BUILD_VERSION,
  });
}

/**
 * Signals a video change to Mux for subsequent plays in the queue
 * (autoplay between episodes). This avoids creating a new Mux session.
 *
 * **Every field the new view needs must be passed here** — the viewinit reset
 * described at the top of this file clears `video_`-prefixed keys as well as
 * `view_` ones, so a value sent once at init is absent from the second play
 * onward.
 */
export function signalVideoChange(
  videoId: string,
  videoTitle: string,
  videoEncodingVariant: string,
): void {
  if (muxDisabled || !muxHandle) {
    return;
  }

  muxHandle.emit('videochange', {
    video_id: videoId,
    video_title: videoTitle,
    video_encoding_variant: videoEncodingVariant,
    view_client_application_name: 'cast',
    view_client_application_version: Config.BUILD_VERSION,
  });

  lastRendition = null;
  Logger.debug(LOG_TAG, 'Mux videochange', { videoId, videoTitle });
}

/**
 * Reports the active video rendition (resolution / bitrate) to Mux Data.
 *
 * **Why this exists.** The `@mux/mux-data-chromecast` SDK auto-emits a
 * `renditionchange` on the CAF `BITRATE_CHANGED` event, but on the Cast path it
 * only carries `video_source_bitrate` — it never sends the width/height, so
 * **Video Width/Height arrive "unset"** in the Mux dashboard. This fills that
 * gap by emitting the full rendition read from `playerManager.getStats()`
 * (`width`, `height`, `streamBandwidth` — already in bit/sec, no conversion).
 *
 * No-op when Mux is disabled, before init, or when no video track is active yet
 * (`width`/`height` unset). Consecutive identical renditions are deduped so the
 * frequent `BITRATE_CHANGED` firings don't spam the beacon.
 */
export function signalRenditionChange(rendition: {
  width: number | undefined;
  height: number | undefined;
  bitrate?: number | undefined;
  codec?: string | undefined;
}): void {
  if (muxDisabled || !muxHandle) {
    return;
  }

  const { width, height, bitrate, codec } = rendition;

  // getStats() has no active video track yet (e.g. right at
  // PLAYER_LOAD_COMPLETE, before the first frame is decoded).
  if (!width || !height || width <= 0 || height <= 0) {
    return;
  }

  const hasBitrate = bitrate != null && !Number.isNaN(bitrate);

  // Dedupe: BITRATE_CHANGED can re-fire with the same variant.
  const key = `${width}x${height}@${hasBitrate ? bitrate : ''}`;
  if (key === lastRendition) {
    return;
  }
  lastRendition = key;

  const data: Record<string, string> = {
    video_source_width: String(width),
    video_source_height: String(height),
  };
  if (hasBitrate) {
    data.video_source_bitrate = String(bitrate);
  }
  if (codec) {
    data.video_source_codec = codec;
  }

  muxHandle.emit('renditionchange', data);

  Logger.debug(LOG_TAG, 'Mux renditionchange', data);
}

/**
 * Reports a playback error to Mux Data with an enriched, readable message.
 *
 * Replaces the SDK's automatic error tracking (disabled in {@link initMux}),
 * which reported errors without a usable message. Call this from the CAF
 * `ERROR` handler with a message built from the detailed/Shaka error labels.
 *
 * @param code    - CAF `detailedErrorCode` (becomes `player_error_code`)
 * @param message - Readable description (becomes `player_error_message`)
 * @param context - Optional extra context (becomes `player_error_context`)
 */
export function emitMuxError(
  code: number | undefined,
  message: string,
  context?: string,
): void {
  if (muxDisabled || !muxHandle) {
    return;
  }

  const data: Record<string, string> = {
    player_error_code: code != null ? String(code) : '',
    player_error_message: message,
  };
  if (context) {
    data.player_error_context = context;
  }

  muxHandle.emit('error', data);

  Logger.debug(LOG_TAG, 'Mux error', data);
}
